import { useState, useEffect } from 'react';
import { getProductImage } from '../../utils/imageStorage';
import LocalImageUploader from '../../components/feature/LocalImageUploader';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

export default function ProduitBouleNoelPagesLivre() {
  const productId = 'boule-noel-pages-livre';
  const defaultImage = 'https://public.readdy.ai/ai/img_res/bb1503a4c3e38751504d1d94efe52011.jpg';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<'1-boule' | 'lot-3'>('1-boule');
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  const handleImageUpdate = (newImageUrl: string) => {
    setProductImage(newImageUrl || defaultImage);
    setShowImageUploader(false);
    window.location.reload();
  };

  // SEO avec React 19
  useEffect(() => {
    document.title = 'Boule de Noël artisanale en livre plié – Les Carnets de Lolotte';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Décoration de Noël faite main en pages de livre recyclées. Pièce unique. Idéal pour un sapin poétique et chaleureux.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'boule de Noël, livre plié, déco de Noël, artisanal, fait main, vintage');
    }
    
    const linkCanonical = document.querySelector('link[rel="canonical"]');
    if (linkCanonical) {
      linkCanonical.setAttribute('href', 'https://lescarnetsdelolotte.fr/produit-boule-noel-pages-livre');
    }
  }, []);

  const variants = {
    '1-boule': {
      name: '1 boule',
      price: 4.00,
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/6c8ed57cc94baac9e7088a44ece36c1a.png',
    },
    'lot-3': {
      name: 'Lot de 3 boules',
      price: 10.00,
      originalPrice: 12.00,
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/6c8ed57cc94baac9e7088a44ece36c1a.png',
    }
  };

  const currentPrice = variants[selectedVariant].price;

  const handleAddToCart = () => {
    addItem({
      id: `boule-noel-pages-livre-${selectedVariant}`,
      name: `Boule de Noël en pages de livre – ${variants[selectedVariant].name}`,
      price: currentPrice,
      quantity: quantity,
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/6c8ed57cc94baac9e7088a44ece36c1a.png'
    });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleBuyNow = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-stripe-checkout-live`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            items: [
              {
                name: `Boule de Noël en pages de livre – ${variants[selectedVariant].name}`,
                price: currentPrice,
                quantity: quantity,
                image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/6c8ed57cc94baac9e7088a44ece36c1a.png'
              }
            ]
          })
        }
      );

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Erreur lors de la création de la session:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <Header />

      <main className="flex-grow">
        {/* Notification */}
        {showNotification && (
          <div className="fixed top-24 right-6 bg-[#C6A996] text-white px-6 py-4 rounded-lg shadow-lg z-50 animate-fade-in">
            <div className="flex items-center gap-3">
              <i className="ri-check-line text-xl"></i>
              <span className="font-medium">Produit ajouté au panier !</span>
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="bg-white border-b border-[#E8D5C4]">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center gap-2 text-sm text-[#8B7355]">
              <a href="/" className="hover:text-[#C6A996] transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <a href="/boutique" className="hover:text-[#C6A996] transition-colors">Boutique</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <a href="/noel" className="hover:text-[#C6A996] transition-colors">Collection de Noël</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-[#C6A996] font-medium">Boule de Noël en pages de livre</span>
            </div>
          </div>
        </div>

        {/* Product Section */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Image */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/6c8ed57cc94baac9e7088a44ece36c1a.png"
                  alt="Boule de Noël en pages de livre – Fait main"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute top-6 left-6 bg-[#B97A72] text-white px-4 py-2 rounded-full text-sm font-medium shadow-md">
                Nouveau 🎄
              </div>
            </div>

            {/* Product Info */}
            <div className="lg:sticky lg:top-24">
              <div className="mb-4">
                <span className="inline-block bg-[#FFF8F5] text-[#C49B86] px-4 py-2 rounded-full text-sm font-medium">
                  Collection de Noël • Boules de Noël
                </span>
              </div>

              <h1 className="text-4xl font-serif text-[#8B7355] mb-4">
                Boule de Noël en pages de livre – Fait main
              </h1>

              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-3xl font-bold text-[#C6A996]">{currentPrice} €</span>
                {selectedVariant === 'lot-3' && (
                  <span className="text-sm text-[#8B7355]">(soit 3,33 € / boule)</span>
                )}
              </div>

              <div className="prose prose-lg text-[#8B7355] mb-8 leading-relaxed">
                <p>
                  Découvrez cette boule de Noël entièrement faite main à partir de véritables pages de livre recyclées.
                  Chaque pièce est unique, délicate et idéale pour une décoration chaleureuse, poétique et authentique.
                </p>
                <ul className="space-y-2 mt-4">
                  <li className="flex items-start gap-2">
                    <span className="text-[#C6A996]">🎀</span>
                    <span>Ruban rouge à motifs flocons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C6A996]">📚</span>
                    <span>Pages de livre vintage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C6A996]">✨</span>
                    <span>Fabrication artisanale, modèle unique</span>
                  </li>
                </ul>
                <p className="mt-4">
                  Parfaite pour décorer un sapin, une branche, une couronne ou offrir un cadeau original.
                </p>
              </div>

              {/* Variants */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#8B7355] mb-3">
                  Choisissez votre option :
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedVariant('1-boule')}
                    className={`flex-1 px-6 py-4 rounded-xl border-2 transition-all whitespace-nowrap ${
                      selectedVariant === '1-boule'
                        ? 'border-[#C6A996] bg-[#FFF8F5] text-[#C6A996] font-semibold'
                        : 'border-[#E8D5C4] bg-white text-[#8B7355] hover:border-[#C6A996]'
                    }`}
                  >
                    <div className="text-center">
                      <div className="font-semibold">1 boule</div>
                      <div className="text-sm mt-1">4 €</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setSelectedVariant('lot-3')}
                    className={`flex-1 px-6 py-4 rounded-xl border-2 transition-all whitespace-nowrap relative ${
                      selectedVariant === 'lot-3'
                        ? 'border-[#C6A996] bg-[#FFF8F5] text-[#C6A996] font-semibold'
                        : 'border-[#E8D5C4] bg-white text-[#8B7355] hover:border-[#C6A996]'
                    }`}
                  >
                    <div className="absolute -top-2 -right-2 bg-[#B97A72] text-white text-xs px-2 py-1 rounded-full">
                      -17%
                    </div>
                    <div className="text-center">
                      <div className="font-semibold">Lot de 3</div>
                      <div className="text-sm mt-1">10 €</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#8B7355] mb-3">
                  Quantité :
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center bg-white border-2 border-[#E8D5C4] rounded-lg hover:border-[#C6A996] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-subtract-line text-xl text-[#8B7355]"></i>
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 h-12 text-center border-2 border-[#E8D5C4] rounded-lg text-[#8B7355] font-semibold focus:outline-none focus:border-[#C6A996]"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center bg-white border-2 border-[#E8D5C4] rounded-lg hover:border-[#C6A996] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-line text-xl text-[#8B7355]"></i>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleAddToCart}
                  className="w-full bg-[#C6A996] text-white py-4 rounded-xl font-semibold hover:bg-[#B89985] transition-colors shadow-md whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Ajouter au panier
                </button>
                <button
                  onClick={handleBuyNow}
                  className="w-full bg-[#8B7355] text-white py-4 rounded-xl font-semibold hover:bg-[#7A6449] transition-colors shadow-md whitespace-nowrap"
                >
                  <i className="ri-flashlight-line mr-2"></i>
                  Acheter maintenant
                </button>
              </div>

              {/* Additional Info */}
              <div className="mt-8 space-y-4 bg-white rounded-xl p-6 border border-[#E8D5C4]">
                <div className="flex items-start gap-3">
                  <i className="ri-hand-heart-line text-2xl text-[#C6A996]"></i>
                  <div>
                    <h3 className="font-semibold text-[#8B7355] mb-1">Fait main avec amour</h3>
                    <p className="text-sm text-[#8B7355]/80">Chaque pièce est unique et réalisée artisanalement</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-recycle-line text-2xl text-[#C6A996]"></i>
                  <div>
                    <h3 className="font-semibold text-[#8B7355] mb-1">Matériaux recyclés</h3>
                    <p className="text-sm text-[#8B7355]/80">Pages de livres vintage soigneusement sélectionnées</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-gift-line text-2xl text-[#C6A996]"></i>
                  <div>
                    <h3 className="font-semibold text-[#8B7355] mb-1">Idée cadeau originale</h3>
                    <p className="text-sm text-[#8B7355]/80">Parfait pour offrir ou se faire plaisir</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-serif text-[#8B7355] text-center mb-12">
              Vous aimerez aussi
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Boule de Noël argent pailletée',
                  price: '4 €',
                  image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/1953d1495cd5e8d1c233a6c576865ce6.png',
                  link: '/produit-boule-noel-argent-pailletee'
                },
                {
                  name: 'Sapin de Noël en livre plié',
                  price: '15 €',
                  image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/sapin-noel-livre-plie.png',
                  link: '/produit-sapin-noel-livre-plie'
                },
                {
                  name: 'Lot de 2 sapins de Noël',
                  price: '25 €',
                  image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/lot-2-sapins-noel.png',
                  link: '/produit-lot-2-sapins-noel-livre-plie'
                }
              ].map((product, index) => (
                <a
                  key={index}
                  href={product.link}
                  className="group bg-[#FFF9F7] rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold text-[#8B7355] mb-2 group-hover:text-[#C6A996] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-[#C6A996] font-bold">{product.price}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
